import { getConfig } from '../lib/config'

export type SocialProvider = 'google' | 'facebook'

export type SocialAuthTokens = {
  provider: SocialProvider
  idToken?: string
  accessToken?: string
}

type GoogleAccounts = {
  oauth2: {
    initTokenClient: (config: {
      client_id: string
      scope: string
      callback: (response: {
        access_token?: string
        error?: string
        error_description?: string
      }) => void
      error_callback?: (error: { type?: string; message?: string }) => void
    }) => { requestAccessToken: (override?: { prompt?: string }) => void }
  }
}

type FacebookAuthResponse = {
  accessToken?: string
  expiresIn?: string
  signedRequest?: string
  userID?: string
}

type FacebookSDK = {
  init: (config: { appId: string; cookie?: boolean; xfbml?: boolean; version: string }) => void
  login: (
    callback: (response: { status: string; authResponse?: FacebookAuthResponse }) => void,
    options?: { scope?: string; return_scopes?: boolean },
  ) => void
  getLoginStatus: (
    callback: (response: { status: string; authResponse?: FacebookAuthResponse }) => void,
  ) => void
}

declare global {
  interface Window {
    google?: { accounts: GoogleAccounts }
    FB?: FacebookSDK
    fbAsyncInit?: () => void
  }
}

const googleScriptSrc = 'https://accounts.google.com/gsi/client'
const facebookScriptSrc = 'https://connect.facebook.net/en_US/sdk.js'

let googleScriptPromise: Promise<void> | null = null
let facebookScriptPromise: Promise<void> | null = null
let facebookInitializedForAppId: string | null = null

function loadScript(src: string, id: string): Promise<void> {
  const existing = document.getElementById(id) as HTMLScriptElement | null
  if (existing) {
    if ((existing as any)._loaded) return Promise.resolve()
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), {
        once: true,
      })
    })
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.id = id
    script.src = src
    script.async = true
    script.defer = true
    script.onload = () => {
      ;(script as any)._loaded = true
      resolve()
    }
    script.onerror = () => reject(new Error(`Failed to load ${src}`))
    document.head.appendChild(script)
  })
}

export function isGoogleAuthConfigured() {
  return !!getConfig().googleClientId
}

export function isFacebookAuthConfigured() {
  return !!getConfig().facebookAppId
}

export function isAnySocialAuthConfigured() {
  return isGoogleAuthConfigured() || isFacebookAuthConfigured()
}

async function ensureGoogleScript() {
  if (!googleScriptPromise) {
    googleScriptPromise = loadScript(googleScriptSrc, 'google-gsi-client')
  }
  await googleScriptPromise
  if (!window.google?.accounts?.oauth2) {
    throw new Error('Google Identity Services failed to initialize')
  }
}

async function ensureFacebookSDK(appId: string) {
  if (!window.FB) {
    if (!facebookScriptPromise) {
      facebookScriptPromise = new Promise((resolve, reject) => {
        const previousInit = window.fbAsyncInit
        window.fbAsyncInit = () => {
          previousInit?.()
          resolve()
        }
        loadScript(facebookScriptSrc, 'facebook-jssdk')
          .then(() => {
            // Cached SDK may already be ready without calling fbAsyncInit again.
            if (window.FB) resolve()
          })
          .catch(reject)
      })
    }
    await facebookScriptPromise
  }

  if (!window.FB) {
    throw new Error('Facebook SDK failed to initialize')
  }

  if (facebookInitializedForAppId !== appId) {
    window.FB.init({
      appId,
      cookie: true,
      xfbml: false,
      version: 'v21.0',
    })
    facebookInitializedForAppId = appId
  }
}

export async function signInWithGoogle(): Promise<SocialAuthTokens> {
  const clientId = getConfig().googleClientId
  if (!clientId) throw new Error('Google sign-in is not configured')

  await ensureGoogleScript()

  return new Promise((resolve, reject) => {
    const client = window.google!.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: 'openid email profile',
      callback: (response) => {
        if (response.error || !response.access_token) {
          reject(
            new Error(
              response.error_description || response.error || 'Google sign-in was cancelled',
            ),
          )
          return
        }
        resolve({ provider: 'google', accessToken: response.access_token })
      },
      error_callback: (error) => {
        reject(new Error(error.message || error.type || 'Google sign-in failed'))
      },
    })
    client.requestAccessToken({ prompt: 'select_account' })
  })
}

export async function signInWithFacebook(): Promise<SocialAuthTokens> {
  const appId = getConfig().facebookAppId
  if (!appId) throw new Error('Facebook sign-in is not configured')

  await ensureFacebookSDK(appId)

  return new Promise((resolve, reject) => {
    window.FB!.login(
      (response) => {
        if (response.status !== 'connected' || !response.authResponse?.accessToken) {
          reject(new Error('Facebook sign-in was cancelled'))
          return
        }
        resolve({
          provider: 'facebook',
          accessToken: response.authResponse.accessToken,
        })
      },
      { scope: 'email,public_profile', return_scopes: true },
    )
  })
}

export async function signInWithProvider(provider: SocialProvider): Promise<SocialAuthTokens> {
  if (provider === 'google') return signInWithGoogle()
  return signInWithFacebook()
}
